import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { recordLogout } from '../../services/api';

const EmployeeMenu = ({ user, onLogout }) => {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  const handleLogout = async () => {
    try {
      // Record logout event in attendance table
      await recordLogout();
    } catch (error) {
      console.error('Failed to record logout event:', error);
      // We don't want to prevent logout even if recording fails
    } finally {
      // Call the parent logout function
      onLogout();
    }
  };

  const go = (path) => {
    setOpen(false);
    navigate(path);
  };

  // Only show employee-specific menu items for non-admin users
  const isEmployee = user?.role !== 'admin';

  return (
    <header className="bg-white border-b shadow-sm">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between h-14 items-center">
          <div className="flex items-center space-x-2 sm:space-x-4">
            <div className="flex-shrink-0 flex items-center cursor-pointer" onClick={() => go('/employee')}>
              {/* Custom Fuel Pump Logo with Background */}
              <div className="relative h-9 w-9 sm:h-10 sm:w-10 rounded-lg bg-gradient-to-br from-orange-400 via-red-500 to-red-600 shadow-md flex items-center justify-center">
                <svg className="h-6 w-6 sm:h-7 sm:w-7 text-white" fill="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path d="M3 2h10c.55 0 1 .45 1 1v15c0 .55-.45 1-1 1H3c-.55 0-1-.45-1-1V3c0-.55.45-1 1-1zm1 2v5h8V4H4zm0 7v6h8v-6H4z" fill="#FFF"/>
                  <path d="M18 7.17L16.83 6 15 7.83 13.17 6 12 7.17l2 2v8.66c0 1.2.8 2.17 1.67 2.17h.66c.87 0 1.67-.97 1.67-2.17V9.17l2-2zM16.5 17h-1v-6h1v6z" fill="#FFD700"/>
                  <circle cx="8" cy="15.5" r="1.5" fill="#90EE90"/>
                </svg>
              </div>
              <span className="ml-2 sm:ml-3 text-sm sm:text-base md:text-xl font-bold text-gray-900 hover:text-primary-600 transition-colors">Employee Manager</span>
            </div>
            {isEmployee && (
              <nav className="hidden md:flex gap-2 lg:gap-3">
                <button onClick={() => go('/employee/entry')} className={`text-xs sm:text-sm whitespace-nowrap ${location.pathname === '/employee/entry' ? 'text-primary-600 font-medium' : 'text-gray-700 hover:text-primary-600'}`}>+ Entry</button>
                <button onClick={() => go('/employee/fuel-sales')} className={`text-xs sm:text-sm whitespace-nowrap ${location.pathname === '/employee/fuel-sales' ? 'text-primary-600 font-medium' : 'text-gray-700 hover:text-primary-600'}`}>Sales</button>
                <button onClick={() => go('/employee/attendance')} className={`text-xs sm:text-sm whitespace-nowrap ${location.pathname === '/employee/attendance' ? 'text-primary-600 font-medium' : 'text-gray-700 hover:text-primary-600'}`}>Attendance</button>
              </nav>
            )}
          </div>

          <div className="flex items-center space-x-2 sm:space-x-3">
            <div className="hidden md:flex md:items-center md:space-x-2">
              <div className="text-xs sm:text-sm text-gray-700 truncate max-w-[120px] sm:max-w-none">{user?.name}</div>
              <button onClick={handleLogout} className="text-xs sm:text-sm text-red-600 whitespace-nowrap">Sign out</button>
            </div>

            {/* Mobile menu button */}
            <div className="md:hidden">
              <button onClick={() => setOpen(!open)} className="inline-flex items-center justify-center p-2 rounded-md text-gray-700 hover:bg-gray-100">
                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d={open ? 'M6 18L18 6M6 6l12 12' : 'M4 6h16M4 12h16M4 18h16'} />
                </svg>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Mobile panel */}
      {open && (
        <div className="md:hidden bg-white border-t">
          <div className="px-4 py-4 space-y-3">
            <div className="flex items-center">
              {/* Custom Fuel Pump Logo with Background */}
              <div className="relative h-10 w-10 rounded-lg bg-gradient-to-br from-orange-400 via-red-500 to-red-600 shadow-md flex items-center justify-center">
                <svg className="h-7 w-7 text-white" fill="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path d="M3 2h10c.55 0 1 .45 1 1v15c0 .55-.45 1-1 1H3c-.55 0-1-.45-1-1V3c0-.55.45-1 1-1zm1 2v5h8V4H4zm0 7v6h8v-6H4z" fill="#FFF"/>
                  <path d="M18 7.17L16.83 6 15 7.83 13.17 6 12 7.17l2 2v8.66c0 1.2.8 2.17 1.67 2.17h.66c.87 0 1.67-.97 1.67-2.17V9.17l2-2zM16.5 17h-1v-6h1v6z" fill="#FFD700"/>
                  <circle cx="8" cy="15.5" r="1.5" fill="#90EE90"/>
                </svg>
              </div>
              <span className="ml-2 text-lg font-bold text-gray-900">Employee Manager</span>
            </div>
            <div className="bg-gray-100 px-3 py-2 rounded-md">
              <div className="text-sm font-medium text-gray-900">{user?.name}</div>
              <div className="text-xs text-gray-500">
                {user?.role === 'admin' ? 'Administrator' : 'Employee'}
              </div>
            </div>
            {isEmployee && (
              <>
                <button onClick={() => go('/employee/entry')} className={`w-full text-left py-2 px-2 rounded ${location.pathname === '/employee/entry' ? 'bg-primary-50 text-primary-600 font-medium' : 'hover:bg-gray-50'}`}>+ Entry</button>
                <button onClick={() => go('/employee/fuel-sales')} className={`w-full text-left py-2 px-2 rounded ${location.pathname === '/employee/fuel-sales' ? 'bg-primary-50 text-primary-600 font-medium' : 'hover:bg-gray-50'}`}>View Sales</button>
                <button onClick={() => go('/employee/attendance')} className={`w-full text-left py-2 px-2 rounded ${location.pathname === '/employee/attendance' ? 'bg-primary-50 text-primary-600 font-medium' : 'hover:bg-gray-50'}`}>Attendance</button>
              </>
            )}
            <button onClick={handleLogout} className="w-full text-left py-2 px-2 text-red-600 rounded hover:bg-gray-50">Sign out</button>
          </div>
        </div>
      )}
    </header>
  );
};

export default EmployeeMenu;